import { validPaletteCommands } from './paletteCommands.js';
import { defaultPreferences, type DockZone, type Preferences, type ToolbarId, type ToolbarPlacement, type WorkspaceLayout } from './contracts.js';

export const toolbarNames: Record<ToolbarId, string> = {
  commands: 'Main command bar', models: 'Open-model tabs', diagram: 'Diagram commands',
  navigation: 'Move, select and detail', layout: 'Layout and placement', export: 'Diagram export',
};
export const dockNames: Record<DockZone, string> = {
  home: 'Default position', header: 'Between header and commands', commands: 'Below main command bar', 'header-secondary': 'Second top docking strip', tabs: 'Below model tabs',
  diagram: 'Above diagram', top: 'Canvas top', bottom: 'Canvas bottom',
  left: 'Canvas left', right: 'Canvas right', floating: 'Float in workspace',
};
export const interfaceSizeScale: Record<Preferences['startupInterfaceSize'], number> = { small: 0.85, medium: 1, large: 1.15 };
export const interfaceScaleLimits = { min: 0.5, max: 2, step: 0.1 } as const;
export function normalizeInterfaceScale(value: number) {
  return Math.min(2, Math.max(0.5, Math.round((Number.isFinite(value) ? value : 1) * 10) / 10));
}
export function interfaceScaleForPreferences(preferences: Pick<Preferences, 'interfaceScale' | 'startupInterfaceSize'>) {
  return normalizeInterfaceScale(preferences.interfaceScale ?? interfaceSizeScale[preferences.startupInterfaceSize] ?? 1);
}
/** Returns a fresh snapshot; resetting the workspace never mutates the user's preset. */
export function builtInWorkspaceLayout(): WorkspaceLayout { return structuredClone(defaultPreferences.workspaceLayout); }
/** Hiding a palette changes only visibility, so docking and floating coordinates survive. */
export function toolbarPlacement(layout: WorkspaceLayout, id: ToolbarId): ToolbarPlacement {
  return { visible: true, zone: 'home', ...layout.toolbars[id] };
}
export function toggleToolbarVisibility(layout: WorkspaceLayout, id: ToolbarId): WorkspaceLayout {
  const placement = toolbarPlacement(layout, id);
  return { ...layout, toolbars: { ...layout.toolbars, [id]: { ...placement, visible: !placement.visible } } };
}
export const movableToolbars: ToolbarId[] = ['commands', 'models', 'diagram', 'navigation', 'layout', 'export'];
export function canDockToolbar(id: ToolbarId, zone: DockZone) {
  return movableToolbars.includes(id) || zone === 'home';
}
export function validWorkspaceLayout(value: unknown): value is WorkspaceLayout {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as any;
  if (Object.keys(v).some(k => !['version', 'locked', 'toolbars', 'explorerWidth', 'propertiesWidth', 'commands'].includes(k)) || (v.version !== undefined && v.version !== 1) || typeof v.locked !== 'boolean' || !v.toolbars || typeof v.toolbars !== 'object' || Array.isArray(v.toolbars)) return false;
  if (v.commands !== undefined && !validPaletteCommands(v.commands)) return false;
  if (!['explorerWidth', 'propertiesWidth'].every(k => v[k] === undefined || typeof v[k] === 'number' && Number.isFinite(v[k]) && v[k] >= 160 && v[k] <= 1600)) return false;
  return Object.entries(v.toolbars).every(([id, raw]) => {
    const entry = raw as any;
    return Object.hasOwn(toolbarNames, id) && entry && typeof entry === 'object' && !Array.isArray(entry) &&
      Object.keys(entry).every(k => ['visible', 'zone', 'x', 'y', 'order'].includes(k)) &&
      typeof entry.visible === 'boolean' && Object.hasOwn(dockNames, entry.zone) && canDockToolbar(id as ToolbarId, entry.zone) &&
      ['x', 'y'].every(k => (entry.zone !== 'floating' && entry[k] === undefined) || typeof entry[k] === 'number' && Number.isFinite(entry[k]) && entry[k] >= 0 && entry[k] <= 100000) &&
      (entry.order === undefined || Number.isInteger(entry.order) && entry.order >= 0 && entry.order <= 20);
  });
}

/** Invalid/obsolete saved state falls back without changing model or editor state. */
export function normalizeWorkspaceLayout(value: unknown): WorkspaceLayout {
  return validWorkspaceLayout(value) ? structuredClone(value) : builtInWorkspaceLayout();
}
export function startupWorkspaceLayout(preferences: Pick<Preferences, 'workspaceLayout' | 'personalWorkspaceLayout'>): WorkspaceLayout {
  return validWorkspaceLayout(preferences.personalWorkspaceLayout)
    ? structuredClone(preferences.personalWorkspaceLayout) : normalizeWorkspaceLayout(preferences.workspaceLayout);
}
export function captureWorkspacePreset(layout: WorkspaceLayout): WorkspaceLayout { return normalizeWorkspaceLayout(layout); }
export function toolbarLockAction(layout: WorkspaceLayout) { return layout.locked ? 'Unlock toolbar positions' : 'Lock toolbar positions'; }

/** Move in the visible dock order, not portal insertion/DOM order. */
export function updateToolbarPlacement(layout: WorkspaceLayout, id: ToolbarId, patch: Partial<ToolbarPlacement>): WorkspaceLayout {
  const placement = { ...toolbarPlacement(layout, id), ...patch };
  const toolbars = { ...layout.toolbars };
  if ((patch.zone !== undefined || patch.order !== undefined) && placement.zone !== 'home' && placement.zone !== 'floating') {
    const allPeers = movableToolbars.filter(key => key !== id && toolbarPlacement(layout, key).zone === placement.zone)
      .sort((a, b) => (toolbarPlacement(layout, a).order ?? movableToolbars.indexOf(a)) - (toolbarPlacement(layout, b).order ?? movableToolbars.indexOf(b)));
    const peers = allPeers.filter(key => toolbarPlacement(layout, key).visible);
    const hiddenPeers = allPeers.filter(key => !toolbarPlacement(layout, key).visible);
    const at = Math.min(peers.length, Math.max(0, patch.order ?? peers.length));
    peers.splice(at, 0, id);
    [...peers, ...hiddenPeers].forEach((key, order) => { toolbars[key] = { ...(key === id ? placement : toolbarPlacement(layout, key)), order }; });
  } else toolbars[id] = placement;
  // Coordinates have no meaning in a dock and must not leak stale offsets into floating CSS.
  if (placement.zone !== 'floating') { delete toolbars[id]!.x; delete toolbars[id]!.y; }
  return { ...layout, version: 1, toolbars };
}

export type DockItemBounds = { id: string; left: number; top: number; width: number; height: number; order: number };
/** Wrapped horizontal strips are ordered by row, then by the visible midpoint in that row. */
export function dockInsertion(items: DockItemBounds[], x: number, y: number, vertical = false): { order: number; before?: string } {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  for (let order = 0; order < sorted.length; order++) {
    const item = sorted[order]!;
    const before = vertical ? y < item.top + item.height / 2
      : y < item.top || y <= item.top + item.height && x < item.left + item.width / 2;
    if (before) return { order, before: item.id };
  }
  return { order: sorted.length };
}

export function clampPanelWidth(width: number, viewportWidth: number, side: 'explorer' | 'properties' = 'explorer') {
  const minimum = side === 'explorer' ? 180 : 240;
  const maximum = Math.max(minimum, Math.min(1200, viewportWidth * 0.6));
  return Math.round(Math.max(minimum, Math.min(Number.isFinite(width) ? width : minimum, maximum)));
}
export function clampToolbarPosition(x: number, y: number, width: number, height: number, viewportWidth: number, viewportHeight: number) {
  return { x: Math.max(8, Math.min(x, viewportWidth - Math.min(width, viewportWidth - 16) - 8)),
    y: Math.max(48, Math.min(y, viewportHeight - Math.min(height, viewportHeight - 56) - 8)) };
}
